import { useEffect, useRef, useState } from 'react'
import { Box, IconButton, Popover, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

// Shared building blocks for the About PACE pages (merchant panel). Pure presentation: no API calls.

export const C = {
  indigo: '#4F46E5',
  violet: '#7C3AED',
  sky: '#0EA5E9',
  teal: '#0D9488',
  emerald: '#10B981',
  amber: '#F59E0B',
  orange: '#F97316',
  rose: '#F43F5E',
  slate: '#0F172A',
  ink: '#1E293B',
  muted: '#64748B',
  line: '#E2E8F0',
  soft: '#F8FAFC',
}

export const grad = (a, b) => `linear-gradient(135deg, ${a} 0%, ${b} 100%)`

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`
export const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
`
export const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(79,70,229,.45); }
  70%  { box-shadow: 0 0 0 14px rgba(79,70,229,0); }
  100% { box-shadow: 0 0 0 0 rgba(79,70,229,0); }
`
export const dashFlow = keyframes`
  to { stroke-dashoffset: -24; }
`
export const shimmer = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`
export const spin = keyframes`
  to { transform: rotate(360deg); }
`
export const growX = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`

const reduceMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

// True once the element has scrolled into view (fires once).
export function useInView(threshold = 0.2) {
  const ref = useRef(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || seen) return undefined
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect() } }, { threshold })
    io.observe(el)
    return () => io.disconnect()
  }, [seen, threshold])
  return [ref, seen]
}

// Fades + lifts its children in when scrolled into view.
export function Reveal({ children, delay = 0, sx }) {
  const [ref, seen] = useInView(0.12)
  return (
    <Box
      ref={ref}
      sx={{
        opacity: seen ? 1 : 0,
        animation: seen && !reduceMotion() ? `${fadeUp} .7s cubic-bezier(.2,.7,.2,1) ${delay}ms both` : 'none',
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

// Number that counts up from 0 once visible.
export function CountUp({ to, decimals = 0, prefix = '', suffix = '', duration = 1400 }) {
  const [ref, seen] = useInView(0.3)
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (!seen) return undefined
    if (reduceMotion()) { setVal(to); return undefined }
    let raf
    const t0 = performance.now()
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration)
      setVal(to * (1 - Math.pow(1 - p, 3)))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [seen, to, duration])
  const txt = val.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  return <span ref={ref}>{prefix}{txt}{suffix}</span>
}

// Small (i) button that opens a plain-language explanation.
export function InfoTip({ title, children, color = C.indigo, size = 'small' }) {
  const [anchor, setAnchor] = useState(null)
  return (
    <>
      <IconButton
        size={size}
        aria-label={`More about ${title}`}
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget) }}
        sx={{ color, p: 0.4, '&:hover': { background: `${color}14` } }}
      >
        <InfoOutlinedIcon fontSize='small' />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { maxWidth: 360, p: 2, borderRadius: 3, borderTop: `4px solid ${color}`, boxShadow: '0 20px 50px rgba(15,23,42,.18)' } }}
      >
        <Typography sx={{ fontWeight: 700, color, mb: 0.75 }}>{title}</Typography>
        <Box sx={{ fontSize: 14, color: C.ink, lineHeight: 1.6, '& p': { m: 0, mb: 1 }, '& p:last-child': { mb: 0 } }}>{children}</Box>
      </Popover>
    </>
  )
}

export function SectionHead({ kicker, title, sub, color = C.indigo, align = 'left' }) {
  return (
    <Box sx={{ mb: 3, textAlign: align }}>
      {kicker && (
        <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color }}>{kicker}</Typography>
      )}
      <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, color: C.slate, lineHeight: 1.2, mt: 0.5 }}>{title}</Typography>
      {sub && <Typography sx={{ color: C.muted, mt: 1, fontSize: 16, maxWidth: align === 'center' ? 760 : 'none', mx: align === 'center' ? 'auto' : 0 }}>{sub}</Typography>}
    </Box>
  )
}

export function Card({ children, sx, accent, hover = true, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        background: '#fff',
        borderRadius: 4,
        border: `1px solid ${C.line}`,
        boxShadow: '0 1px 2px rgba(15,23,42,.04), 0 8px 24px rgba(15,23,42,.05)',
        p: 3,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform .25s ease, box-shadow .25s ease',
        cursor: onClick ? 'pointer' : 'default',
        ...(accent && { '&:before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 4, background: accent } }),
        ...(hover && { '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 18px 40px rgba(15,23,42,.10)' } }),
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

export function IconBubble({ icon, color, size = 48 }) {
  return (
    <Box
      sx={{
        width: size, height: size, borderRadius: '30%', display: 'grid', placeItems: 'center', color: '#fff',
        background: grad(color, `${color}B3`), boxShadow: `0 10px 20px ${color}40`, flexShrink: 0,
        '& svg': { fontSize: size * 0.52 },
      }}
    >
      {icon}
    </Box>
  )
}

export function Pill({ children, color = C.indigo, solid = false }) {
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.35, borderRadius: 99, fontSize: 12, fontWeight: 700,
        color: solid ? '#fff' : color, background: solid ? color : `${color}14`, border: `1px solid ${color}33`, whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  )
}

// Footnote line under a proof point: where the number came from.
export function Source({ children }) {
  return <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 1, fontStyle: 'italic' }}>Source: {children}</Typography>
}
